import { useApolloClient, useMutation, useQuery, useReactiveVar } from '@apollo/client';
import { useCallback, useMemo } from 'react';
import { IDepartment, Identifiable } from '../../../lib/types';
import { ICompanyDepartmentsResult, ICreateDepartmentProps, IUpdateDepartmentProps } from './department.types';
import {
  CREATE_DEPARTMENTS,
  DELETE_DEPARTMENT,
  GET_COMPANY_DEPARTMENTS,
  RESTORE_DEPARTMENT,
  UPDATE_DEPARTMENT
} from './department.queries';
import { updateDepartmentInCache } from './department.utils';
import { IArchivableProps } from '../../../lib/query.types';
import { mergeCacheLists } from '../../../lib/utils';
import { activeCompanyLoadingVar } from '../companies/company.service';
import { IPermanent } from '../request-group/request-group.types';
import { PERMANENT_DELETE_REFETCH_QUERIES } from '../companies/company.const';

export const useCompanyDepartments = () => {
  const activeCompanyLoading = useReactiveVar(activeCompanyLoadingVar);
  const { data, loading, error, called, refetch, networkStatus, client, observable, previousData } = useQuery<ICompanyDepartmentsResult, IArchivableProps>(GET_COMPANY_DEPARTMENTS, {
    fetchPolicy: 'cache-and-network',
    variables: { includeArchived: true }
  });

  const departments = useMemo(() => data?.departments, [data?.departments]);
  const combinedLoading = useMemo(() => loading || activeCompanyLoading, [loading, activeCompanyLoading]);

  return useMemo(() => ({
    loading: combinedLoading,
    error,
    called,
    refetch,
    networkStatus,
    client,
    observable,
    previousData,
    departments
  }), [combinedLoading, error, called, refetch, networkStatus, client, observable, previousData, departments]);
};

export const useCreateDepartments = () => {
  const [mutation, rest] = useMutation<{ createDepartments: IDepartment[] }>(CREATE_DEPARTMENTS, {
    context: { serializationKey: 'MUTATION', tracked: true },
    update: (cache, { data }) => {
      if (data) {
        // Update curr company departments
        const currCompanyDepartments = cache.readQuery<ICompanyDepartmentsResult, IArchivableProps>({
          query: GET_COMPANY_DEPARTMENTS,
          variables: { includeArchived: true }
        })?.departments;

        cache.writeQuery<ICompanyDepartmentsResult, IArchivableProps>({
          query: GET_COMPANY_DEPARTMENTS,
          data: { departments: mergeCacheLists(currCompanyDepartments ?? [], data.createDepartments) },
          variables: { includeArchived: true },
          overwrite: true
        });
      }
    }
  });

  const createDepartments = useCallback((variables: ICreateDepartmentProps) => mutation({ variables }), [mutation]);

  return { createDepartments, ...rest };
};

export const useDeleteDepartment = () => {
  const client = useApolloClient();

  const [mutation, rest] = useMutation<{ deleteDepartment: IDepartment }>(DELETE_DEPARTMENT, {
    context: { serializationKey: 'MUTATION', tracked: true },
    update: (cache, { data }, { variables }) => {
      if (data?.deleteDepartment) {
        updateDepartmentInCache({ cache, department: data.deleteDepartment, remove: variables?.permanent });

        if (variables?.permanent) client.refetchQueries({ include: PERMANENT_DELETE_REFETCH_QUERIES });
      }
    }
  });

  const deleteDepartment = useCallback((variables: Identifiable & IPermanent) => mutation({ variables }), [mutation]);

  return { deleteDepartment, ...rest };
};

export const useRestoreDepartment = () => {
  const [mutation, rest] = useMutation<{ restoreDepartment: IDepartment }>(RESTORE_DEPARTMENT, {
    context: { serializationKey: 'MUTATION', tracked: true },
    update: (cache, { data }) => {
      if (data?.restoreDepartment) updateDepartmentInCache({ cache, department: data.restoreDepartment });
    }
  });

  const restoreDepartment = useCallback((variables: Identifiable) => mutation({ variables }), [mutation]);

  return { restoreDepartment, ...rest };
};

export const useUpdateDepartment = () => {
  const [mutation, rest] = useMutation<{ updateDepartment: IDepartment }>(UPDATE_DEPARTMENT, {
    context: { serializationKey: 'MUTATION', tracked: true },
    update: (cache, { data }) => {
      if (data?.updateDepartment) updateDepartmentInCache({ cache, department: data.updateDepartment });
    }
  });

  const updateDepartment = useCallback((variables: IUpdateDepartmentProps) => mutation({ variables }), [mutation]);

  return { updateDepartment, ...rest };
};
